import { createContext, useContext, useCallback, useEffect, useReducer } from 'react';
import api, { refreshSession } from '@utils/api';

const HR_ROLES = ['super_admin', 'admin', 'hr_manager', 'hr_staff'];

const initialState = {
  user:            null,
  token:           null,
  isLoading:       true,
  isAuthenticated: false,
  error:           null,
};

function authReducer(state, action) {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return { ...state, user: action.user, token: action.token, isAuthenticated: true, isLoading: false, error: null };
    case 'LOGOUT':
      return { ...initialState, isLoading: false };
    case 'REFRESH_SUCCESS':
      return { ...state, token: action.token, isLoading: false };
    case 'SET_LOADING':
      return { ...state, isLoading: action.value };
    case 'SET_ERROR':
      return { ...state, error: action.error, isLoading: false };
    default:
      return state;
  }
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Shared single-flight (from api.js) — the SAME promise the 401
        // interceptor uses, so bootstrap and interceptor never double-refresh
        // and rotate the one-time cookie twice. A cold-start 401 = "no session".
        const { data } = await refreshSession();
        if (cancelled) return;
        api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
        const { data: user } = await api.get('/auth/me');
        if (cancelled) return;
        dispatch({ type: 'LOGIN_SUCCESS', user, token: data.accessToken });
      } catch {
        if (!cancelled) dispatch({ type: 'LOGOUT' });
      }
    })();

    const onExpired = () => dispatch({ type: 'LOGOUT' });
    window.addEventListener('glk-hr:session-expired', onExpired);
    return () => {
      cancelled = true;
      window.removeEventListener('glk-hr:session-expired', onExpired);
    };
  }, []);

  const login = useCallback(async (email, password) => {
    dispatch({ type: 'SET_LOADING', value: true });
    try {
      const { data } = await api.post('/auth/login', { email, password });
      api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
      dispatch({ type: 'LOGIN_SUCCESS', user: data.user, token: data.accessToken });
      return { success: true };
    } catch (err) {
      const error = err.response?.data?.message || 'Login failed';
      dispatch({ type: 'SET_ERROR', error });
      return { success: false, error };
    }
  }, []);

  // Establish a session from an intranet SSO handoff ticket (no password).
  const ssoLogin = useCallback(async (ticket) => {
    dispatch({ type: 'SET_LOADING', value: true });
    try {
      const { data } = await api.post('/auth/sso', { ticket });
      api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
      dispatch({ type: 'LOGIN_SUCCESS', user: data.user, token: data.accessToken });
      return { success: true };
    } catch (err) {
      const error = err.response?.data?.message || 'Single sign-on failed';
      dispatch({ type: 'SET_ERROR', error });
      return { success: false, error };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    delete api.defaults.headers.common['Authorization'];
    dispatch({ type: 'LOGOUT' });
  }, []);

  const isHR   = useCallback(() => !!state.user && HR_ROLES.includes(state.user.role), [state.user]);
  const hasRole = useCallback((roles) => {
    if (!state.user) return false;
    const list = Array.isArray(roles) ? roles : [roles];
    return list.includes(state.user.role);
  }, [state.user]);

  return (
    <AuthContext.Provider value={{ ...state, login, ssoLogin, logout, isHR, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- provider + hook is the standard context pattern
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
